<script setup lang="ts">
/**
 * SceneBusy · 忙碌小剧场：工位上打字赶工
 * ------------------------------------------------------------
 * 舞台 = 开放式工位：木桌 + 发亮的显示器（代码行与闪烁光标）+ 键盘
 * + 敲键盘的双手 + 冒热气的咖啡；地面光斑把工位钉在地上。
 * 人物比例与所有场景统一：头 r6.6、0.8px/viewBox 单位（见 scenes/index.ts）。
 */
defineProps<{ peek?: boolean; hot?: boolean }>()
</script>

<template>
  <span class="sc" aria-hidden="true">
    <i class="floor" />
    <svg class="desk" viewBox="0 0 26 30">
      <defs>
        <linearGradient id="apShirtBusy" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#7A95FF" />
          <stop offset="1" stop-color="#4C6BF0" />
        </linearGradient>
      </defs>
      <!-- 身体（连帽衫，坐在桌后） -->
      <path d="M7.4 26v-6.4c0-2.9 2.4-4.6 5.6-4.6s5.6 1.7 5.6 4.6V26z" fill="url(#apShirtBusy)" />
      <path d="M10.3 16.2q2.7 2.1 5.4 0" stroke="#FFFFFF" stroke-opacity=".55" stroke-width=".9"
            fill="none" stroke-linecap="round" />
      <!-- 桌腿 + 桌面 -->
      <rect x="2.3" y="24.3" width="1.3" height="5.5" fill="#B4794B" />
      <rect x="22.4" y="24.3" width="1.3" height="5.5" fill="#B4794B" />
      <rect x=".6" y="22.6" width="24.8" height="1.7" rx=".85" fill="#C98B5C" stroke="#B4794B" stroke-width=".5" />
      <!-- 桌上的纸 -->
      <rect x="1.6" y="21.7" width="2.6" height=".9" rx=".2" fill="#FFFFFF" stroke="#DCE3EC" stroke-width=".4" />
      <!-- 显示器（支架 → 机身 → 发亮的屏 + 代码行 + 闪烁光标） -->
      <rect x="12.4" y="20.4" width="1.2" height="2.2" fill="#33405A" />
      <rect x="8.3" y="15.4" width="9.4" height="5" rx=".9" fill="#33405A" stroke="#26314A" stroke-width=".5" />
      <rect x="9.1" y="16.2" width="7.8" height="3.4" rx=".5" fill="#BFE0FF" />
      <rect x="9.7" y="16.8" width="4.6" height=".7" rx=".35" fill="#7A95FF" />
      <rect x="9.7" y="17.8" width="3.2" height=".7" rx=".35" fill="#4CC3F0" />
      <rect x="9.7" y="18.8" width="3.9" height=".7" rx=".35" fill="#7FE0A6" />
      <rect x="13.8" y="18.8" width=".8" height=".7" fill="#33405A">
        <animate attributeName="opacity" values="1;1;0;0" keyTimes="0;.5;.5;1" dur="1.1s" repeatCount="indefinite" />
      </rect>
      <!-- 键盘 -->
      <rect x="9.9" y="21.7" width="6.2" height=".8" rx=".35" fill="#E6ECF4" stroke="#C9D2DE" stroke-width=".4" />
      <!-- 敲键盘的双手（快速起落） -->
      <g>
        <animateTransform attributeName="transform" type="translate" values="0 0;0 -.65;0 0"
                          dur=".34s" repeatCount="indefinite" />
        <rect x="8.2" y="20.9" width="2.6" height="1.7" rx=".85" fill="#FFDCB4" stroke="#E6B183" stroke-width=".8" />
      </g>
      <g>
        <animateTransform attributeName="transform" type="translate" values="0 0;0 -.65;0 0"
                          dur=".34s" begin=".17s" repeatCount="indefinite" />
        <rect x="15.2" y="20.9" width="2.6" height="1.7" rx=".85" fill="#FFDCB4" stroke="#E6B183" stroke-width=".8" />
      </g>
      <!-- 咖啡 + 热气 -->
      <rect x="21.7" y="20.2" width="2" height="2.4" rx=".45" fill="#FFFFFF" stroke="#D7DEE8" stroke-width=".5" />
      <path d="M23.75 20.8q.9.3 0 1.2" stroke="#D7DEE8" stroke-width=".6" fill="none" />
      <path d="M22.7 19.6q-.5-.7 0-1.4" stroke="#DCE9F5" stroke-width=".5" fill="none" stroke-linecap="round">
        <animate attributeName="opacity" values=".9;.15;.9" dur="2.2s" repeatCount="indefinite" />
      </path>
      <!-- 头（微微晃，像边打字边琢磨） -->
      <g>
        <animateTransform attributeName="transform" type="rotate"
                          values="-2 13 15;2 13 15;-2 13 15" dur="2.8s" repeatCount="indefinite" />
        <circle cx="13" cy="8.2" r="6.6" fill="#FFDCB4" stroke="#E6B183" stroke-width=".9" />
        <!-- 头发 + 呆毛 -->
        <path d="M6.5 7.7a6.5 6.5 0 0 1 13 0v.8H6.5z" fill="#3A2B21" />
        <path d="M12.5 2.4c1.1-1.6 2.7-1.9 3.5-1.4-1.3.4-2.1 1-2.5 1.8z" fill="#3A2B21" />
        <!-- 眉 -->
        <path d="M9.9 5.7q1.1-.7 2.2-.2M16.1 5.7q-1.1-.7-2.2-.2" stroke="#3A2B21" stroke-width=".7"
              fill="none" stroke-linecap="round" opacity=".75" />
        <!-- 眼（会眨） -->
        <ellipse cx="10.9" cy="8.4" rx="1.5" ry="1.75" fill="#2B2118">
          <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                   dur="3.1s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="15.1" cy="8.4" rx="1.5" ry="1.75" fill="#2B2118">
          <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                   dur="3.1s" repeatCount="indefinite" />
        </ellipse>
        <circle cx="11.5" cy="7.7" r=".5" fill="#FFFFFF" opacity=".9" />
        <circle cx="15.7" cy="7.7" r=".5" fill="#FFFFFF" opacity=".9" />
        <!-- 腮红 -->
        <ellipse cx="8.5" cy="10.8" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
        <ellipse cx="17.5" cy="10.8" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
        <!-- 笑 -->
        <path d="M11.6 11.4a1.5 1.5 0 0 0 2.8 0z" fill="#C05A5A" />
      </g>
    </svg>
  </span>
</template>

<style scoped>
.sc { position: absolute; inset: 0; display: block; font-size: 0; }

/* 地面光斑：代替窗台，把工位钉在地上 */
.floor {
  position: absolute; left: 50%; bottom: 4px;
  width: 30px; height: 7px; margin-left: -15px; border-radius: 50%;
  background: radial-gradient(closest-side, #1a3a662e, transparent);
}
/* 0.8px/viewBox 单位：26×30 → 20.8×24，人物头径与 online 场景一致；
   抬高 5px：坐姿头部较矮，与窗台/走路场景的小人保持同一条头部水平线 */
.desk {
  position: absolute; left: 50%; bottom: 5px;
  width: 20.8px; height: 24px; margin-left: -10.4px;
  filter: drop-shadow(0 1px 1px #1a3a6626);
}
</style>
